import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import useLatest from 'hooks/useLatestRef';
import { QueueState, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { IMachineQueueStateMsg } from 'lib_ts/interfaces/machine-msg/i-machine-queue-state';
import { first } from 'lodash';
import { useEffect, useState } from 'react';
import { createSocket, destroySocket, trySocketEmit } from 'services/socket';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

// listens for queue state msgs and updates the store
const useQueueStateListener = (): void => {
  const current = useAuthStore((s) => s.current);
  const wsConnected = useAuthStore((s) => s.wsConnected);

  const latestCurrent = useLatest(current);

  // stores the most recently received msg
  const [msg, setMsg] = useState<IMachineQueueStateMsg>();

  // listen for msgs and update the appropriate values
  useEffect(() => {
    if (!wsConnected) {
      return;
    }

    const callback = (event: CustomEvent) => {
      const data: IMachineQueueStateMsg = event.detail;

      console.debug(
        `Received queue state message @ ${new Date().toISOString()}:`,
        data
      );

      if (!data.wsIDs.includes(latestCurrent.current.wsID)) {
        console.warn(
          `User ${latestCurrent.current.wsID} is not in the queue > reconnecting socket to rejoin...`
        );

        destroySocket();
        createSocket(latestCurrent.current.token);
        return;
      }

      setMsg(data);

      const { calibrated, canCalibrate, eStopPressed, notify } =
        data.healthStatus;

      useMachineStatusStore.setState({
        activeUser: data.activeUser,
        calibrated: calibrated,
        canCalibrate: calibrated
          ? canCalibrate
          : canCalibrate && notify
            ? 'notify'
            : canCalibrate,

        eStopPressed: eStopPressed && notify ? 'notify' : eStopPressed,
      });

      useMachineStatusStore.getState().lazySetQueueWsIDs(data.wsIDs);
    };

    WebSocketHelper.on(WsMsgType.Misc_MachineQueueState, callback);

    // after starting the listener, request a broadcast
    trySocketEmit(WsMsgType.Misc_MachineQueueState, {
      // no data needed
    });

    return () =>
      WebSocketHelper.remove(WsMsgType.Misc_MachineQueueState, callback);
  }, [wsConnected]);

  // set queue state based on msg + wsID so the server can broadcast 1 msg to all queue members
  useEffect(() => {
    const getState = () => {
      if (!wsConnected || !msg) {
        // default fallback
        return QueueState.UserDC;
        return;
      }

      if (!msg.isMachineConnected) {
        return QueueState.MachineDC;
        return;
      }

      if (first(msg.wsIDs) === current.wsID) {
        return QueueState.Active;
        return;
      }

      if (msg.wsIDs.includes(current.wsID)) {
        return QueueState.Busy;
        return;
      }

      // shouldn't trigger, default fallback
      return QueueState.UserDC;
    };

    useMachineStatusStore.setState({ queueState: getState() });
  }, [wsConnected, current.wsID, msg]);
};

export default useQueueStateListener;
